import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import "chartjs-adapter-date-fns";
import { StyledWrapper, StyledChartContainer, StyledLegendContainer } from "./styled";
import { ITemplateData } from "@framework/types";
import { ChartData, Chart as ChartJS, ChartOptions, Color, Scale } from "chart.js";
import { formatData, formatChartData } from "./chart-utils";
import { lineColors } from "../../../../scheme/components/analytics/constants";
import { useAsu } from "@framework/provider-asu";
import { useIntl } from "react-intl";
import { enGB, ru } from "date-fns/locale";
import { themeProps } from "../../../../../../../components/theme";
import { useToFakeUtcISO } from "../../../../../../../hooks";

import { ExternalTooltip } from "../../../../../../../components/common/chart-plugins";

const locales: { [key: string]: Locale } = { en: enGB, ru };

const getFontFamily = () =>
  typeof themeProps?.options?.typography === "function"
    ? "Roboto"
    : themeProps?.options?.typography?.fontFamily ?? "Roboto";

export interface IChartView {
  data: ITemplateData[];
  chartTitle: string;
  legendContainerId: string;
}

export const ChartView: FC<IChartView> = props => {
  const { data, chartTitle, legendContainerId } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS<"line"> | null>(null);

  const { startTimestamp, endTimestamp } = useAsu();

  const { locale } = useIntl();
  const toFakeUtcISO = useToFakeUtcISO();
  const getLocal = useCallback((date: string) => toFakeUtcISO(date).replace("Z", ""), [toFakeUtcISO]);

  const getLocale = useCallback((): Locale => {
    return locales[locale.toLowerCase()];
  }, [locale]);

  const chartLinesData = useMemo(() => formatData(data, "ValueNum"), [data]);
  const chartLines = useMemo(() => {
    const chartDataResult = formatChartData(chartLinesData, getLocal);
    if (Array.isArray(chartDataResult)) {
      return chartDataResult;
    }
    return [chartDataResult];
  }, [chartLinesData]);

  const chartData = useMemo<ChartData<"line", any[]>>(
    () => ({
      datasets: chartLines.map(({ name, items, units }, i) => ({
        type: "line",
        label: name,
        borderColor: lineColors[i],
        clip: { top: 0, right: 0, bottom: 6, left: 0 },
        data: items.map(item => ({
          x: getLocal(item.date),
          y: item.value,
        })),
        pointHoverBorderWidth: 8,
        yAxisID: units,
      })),
    }),
    [chartLines],
  );

  const getMinDate = useCallback(() => {
    let result: string | undefined;

    chartLines.forEach(({ items }) => {
      items.forEach(({ date }) => {
        if (!result || new Date(getLocal(date)).getTime() < new Date(result).getTime()) {
          result = getLocal(date);
        }
      });
    });

    return result;
  }, [chartLines, getLocal]);

  const getMaxDate = useCallback(() => {
    let result: string | undefined;

    chartLines.forEach(({ items }) => {
      items.forEach(({ date }) => {
        if (!result || new Date(getLocal(date)).getTime() > new Date(getLocal(result)).getTime()) {
          result = getLocal(date);
        }
      });
    });

    return result;
  }, [chartLines, getLocal]);

  const minDateRawStr = getMinDate() ?? startTimestamp;
  const maxDateRawStr = getMaxDate() ?? endTimestamp;
  const minDateRaw = new Date(minDateRawStr);
  const maxDateRaw = new Date(maxDateRawStr);
  const diffMs = maxDateRaw.getTime() - minDateRaw.getTime();
  const paddingMs = diffMs * 0.05;
  const minDate = new Date(minDateRaw.getTime() - paddingMs);
  const maxDate = new Date(maxDateRaw.getTime() + paddingMs);

  const getTimeUnit = useCallback(() => {
    const diffMs = maxDate.getTime() - minDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 365 * 2) return "year";
    if (diffDays > 60) return "month";
    if (diffDays > 14) return "week";
    if (diffDays > 2) return "day";
    if (diffDays > 1) return "hour";
    return "minute";
  }, [minDate, maxDate]);

  const uniqueUnits = useMemo(() => Array.from(new Set(chartLines.map(({ units }) => units))), [chartLines]);

  const yAxisStats = useMemo(
    () =>
      chartLines.reduce<Record<string, { min: number; max: number; hasNegative: boolean }>>((acc, { units, items }) => {
        const values = items
          .map(({ value }) => Number(value))
          .filter((value): value is number => Number.isFinite(value));

        if (values.length === 0) {
          return acc;
        }

        const currentMin = Math.min(...values);
        const currentMax = Math.max(...values);
        const prev = acc[units];

        if (!prev) {
          acc[units] = {
            min: currentMin,
            max: currentMax,
            hasNegative: currentMin < 0,
          };
          return acc;
        }

        acc[units] = {
          min: Math.min(prev.min, currentMin),
          max: Math.max(prev.max, currentMax),
          hasNegative: prev.hasNegative || currentMin < 0,
        };

        return acc;
      }, {}),
    [chartLines],
  );

  const showYAxisLabels =
    (uniqueUnits?.length === 2 && chartLines.every(item => item?.isAmplitudePhase === true)) ||
    uniqueUnits?.length === 1;

  interface IYAxis {
    type: "linear";
    display: boolean;
    position: "left" | "right";
    id: string;
    grace: string;
    afterBuildTicks?: (axis: Scale) => void;
    title: {
      display: boolean;
      text: string;
      font: {
        size: number;
        family: string;
      };
      color: string;
    };
    ticks: {
      font: {
        size: number;
        family: string;
      };
      callback?: (value: number) => number | null;
    };
    grid: {
      drawOnChartArea: boolean;
    };
    offset?: boolean;
    min?: number;
  }

  const yAxes = useMemo(
    () =>
      uniqueUnits.reduce<Record<string, IYAxis>>((acc, units, index) => {
        const axisStats = yAxisStats[units];
        const axisRange = axisStats ? axisStats.max - axisStats.min : 0;
        const axisPadding = axisStats
          ? axisRange > 0
            ? axisRange * 0.1
            : Math.max(Math.abs(axisStats.max), Math.abs(axisStats.min)) * 0.1 || 0.1
          : 0;
        const axisMin = axisStats ? axisStats.min - axisPadding : undefined;

        acc[units] = {
          type: "linear",
          display: showYAxisLabels,
          position: index % 2 === 0 ? "left" : "right",
          id: units,
          grace: "10%",
          ...(axisMin !== undefined ? { min: axisMin } : {}),
          afterBuildTicks: axis => {
            if (axisStats?.hasNegative) {
              return;
            }

            axis.ticks = axis.ticks.filter(({ value }) => Number(value) >= 0);
          },
          title: {
            display: showYAxisLabels,
            text: "",
            font: {
              size: 12,
              family: getFontFamily(),
            },
            color: "rgba(0, 0, 0, 0.87)",
          },
          ticks: {
            font: {
              size: 11,
              family: getFontFamily(),
            },
            callback: (value: number) => {
              const numericValue = Number(value);
              if (Number.isNaN(numericValue)) {
                return null;
              }

              if (units === "ИТС" && (numericValue > 100 || numericValue < 0)) {
                return null;
              }
              return Number(numericValue.toFixed(1));
            },
          },
          grid: {
            drawOnChartArea: index === 0 && showYAxisLabels,
          },
        };
        return acc;
      }, {}),
    [uniqueUnits, showYAxisLabels, yAxisStats],
  );

  const options = useMemo<ChartOptions<"line"> & { plugins: any }>(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      scales: {
        x: {
          min: minDate.getTime(),
          max: maxDate.getTime(),
          adapters: {
            date: {
              locale: getLocale(),
            },
          },
          ticks: {
            maxTicksLimit: 20,
            font: {
              size: 12,
              family: getFontFamily(),
            },
          },
          type: "time" as const,
          time: {
            unit: getTimeUnit() as "minute" | "hour" | "day" | "week" | "month" | "year",
            displayFormats: {
              year: "yyyy",
              month: "MM.yyyy",
              week: "dd.MM.yy",
              day: "dd.MM.yy",
              hour: "dd.MM.yy/HH:mm",
              minute: "HH:mm",
            },
            tooltipFormat: "dd.MM.yy/HH:mm:ss",
          },
        },
        ...yAxes,
      },
      elements: {
        point: {
          borderWidth: 0.5,
        },
      },
      interaction: {
        intersect: false,
        mode: "nearest" as const,
        axis: "xy",
      },
      plugins: {
        title: {
          display: false,
          text: chartTitle,
          font: {
            size: 10,
            family: getFontFamily(),
          },
        },
        alarmLevel: false,
        htmlLegend: {
          containerId: legendContainerId,
          fontFamily: getFontFamily(),
        },
        legend: {
          display: false,
          labels: {
            boxWidth: 10,
            padding: 10,
            font: {
              size: 10,
              family: getFontFamily(),
            },
            generateLabels: (chart: ChartJS<"line">) => {
              const { data } = chart;
              return data.datasets.map((dataset, i: number) => ({
                text: dataset.label || "",
                fillStyle: dataset.borderColor as Color,
                strokeStyle: dataset.borderColor as Color,
                borderRadius: 6,
                usePointStyle: true,
                pointStyle: "circle" as const,
                lineWidth: chart.isDatasetVisible(i) ? 0 : 1,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
                onClick: () => {
                  const meta = chart.getDatasetMeta(i);
                  meta.hidden = meta.hidden ?? !chart.data.datasets[i].hidden;
                  chart.update();
                },
              }));
            },
          },
        },
        tooltip: {
          enabled: false,
          intersect: false,
          mode: "nearest" as const,
          external: ExternalTooltip,
          unitsX: "",
          unitsY: "",
        },
        crosshair: {
          enabled: true,
          line: {
            color: lineColors[0],
            width: 0.5,
          },
          sync: {
            enabled: false,
          },
          zoom: {
            enabled: false,
          },
        },
      },
    }),
    [minDate, maxDate, getLocale, yAxes, uniqueUnits, chartTitle],
  );

  const renderChart = useCallback(() => {
    if (!canvasRef.current) return;

    chartRef.current = new ChartJS<"line">(canvasRef.current, {
      type: "line",
      data: chartData,
      options,
    });
  }, [chartData, options]);

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  }, []);

  useEffect(() => {
    renderChart();
    return destroyChart;
  }, [renderChart, destroyChart]);

  return (
    <StyledWrapper>
      <StyledChartContainer>
        <canvas
          ref={canvasRef}
          role="img"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
          }}
        />
      </StyledChartContainer>
      <StyledLegendContainer id={legendContainerId} />
    </StyledWrapper>
  );
};
